/**
 * Permission Service for Frontend Authorization
 * Provides a centralized way to check user permissions and control UI visibility
 */

import type { User } from '@/types'
import { useAuthStore } from '@/stores'

export enum PermissionType {
  ADMIN = 'ADMIN',

  CREATE_FIRM = 'CREATE_FIRM',
  UPDATE_FIRM = 'UPDATE_FIRM',
  VIEW_FIRM = 'VIEW_FIRM',

  // Firm User Management
  MANAGE_FIRM_USERS = 'MANAGE_FIRM_USERS',
  CREATE_FIRM_USER = 'CREATE_FIRM_USER',
  READ_FIRM_USERS = 'READ_FIRM_USERS',
  UPDATE_FIRM_USER = 'UPDATE_FIRM_USER',
  DELETE_FIRM_USER = 'DELETE_FIRM_USER',
  RESEND_FIRM_USER_INVITATION = 'RESEND_FIRM_USER_INVITATION',

  // Firm Intervenant Management
  MANAGE_FIRM_INTERVENANTS = 'MANAGE_FIRM_INTERVENANTS',
  CREATE_FIRM_INTERVENANT = 'CREATE_FIRM_INTERVENANT',
  READ_FIRM_INTERVENANTS = 'READ_FIRM_INTERVENANTS',
  UPDATE_FIRM_INTERVENANT = 'UPDATE_FIRM_INTERVENANT',
  DELETE_FIRM_INTERVENANT = 'DELETE_FIRM_INTERVENANT',

  // Firm Contact Management
  MANAGE_FIRM_CONTACTS = 'MANAGE_FIRM_CONTACTS',
  CREATE_FIRM_CONTACT = 'CREATE_FIRM_CONTACT',
  READ_FIRM_CONTACTS = 'READ_FIRM_CONTACTS',
  UPDATE_FIRM_CONTACT = 'UPDATE_FIRM_CONTACT',
  DELETE_FIRM_CONTACT = 'DELETE_FIRM_CONTACT',

  // Client dashboard
  VIEW_CLIENT_DASHBOARD = 'VIEW_CLIENT_DASHBOARD',

  VIEW_RECEIPT_TEMPLATES = 'VIEW_RECEIPT_TEMPLATES',

  // Firm Settings
  MANAGE_FIRM_SETTINGS = 'MANAGE_FIRM_SETTINGS',
  VIEW_FIRM_SETTINGS = 'VIEW_FIRM_SETTINGS',
  VIEW_FIRM_ACTIVITY = 'VIEW_FIRM_ACTIVITY',
  VIEW_FIRM_REVENUE = 'VIEW_FIRM_REVENUE',

  CREATE_PROJECT = 'CREATE_PROJECT',
  UPDATE_PROJECT_INFO = 'UPDATE_PROJECT_INFO',
  VIEW_PROJECT_INFO = 'VIEW_PROJECT_INFO',
  ACCESS_PROJECT = 'ACCESS_PROJECT',
  UPDATE_PROJECT_STATUS = 'UPDATE_PROJECT_STATUS',

  CREATE_INTERVENANT = 'CREATE_INTERVENANT',
  UPDATE_INTERVENANT = 'UPDATE_INTERVENANT',
  DELETE_INTERVENANT = 'DELETE_INTERVENANT',
  ACCESS_INTERVENANTS = 'ACCESS_INTERVENANTS',

  UPLOAD_PHOTO = 'UPLOAD_PHOTO',
  DELETE_PHOTO = 'DELETE_PHOTO',
  ACCESS_PHOTO = 'ACCESS_PHOTO',

  UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT',
  DELETE_DOCUMENT = 'DELETE_DOCUMENT',
  ACCESS_DOCUMENT = 'ACCESS_DOCUMENT',

  CREATE_TASK = 'CREATE_TASK',
  UPDATE_TASK = 'UPDATE_TASK',
  DELETE_TASK = 'DELETE_TASK',
  ACCESS_TASKS = 'ACCESS_TASKS',

  CREATE_ISSUE = 'CREATE_ISSUE',
  UPDATE_ISSUE = 'UPDATE_ISSUE',
  DELETE_ISSUE = 'DELETE_ISSUE',
  ACCESS_ISSUES = 'ACCESS_ISSUES',
  VIEW_ISSUE = 'VIEW_ISSUE',
  COMMENT_ISSUE = 'COMMENT_ISSUE',

  CREATE_PHASE = 'CREATE_PHASE',
  UPDATE_PHASE = 'UPDATE_PHASE',
  DELETE_PHASE = 'DELETE_PHASE',
  ACCESS_PHASES = 'ACCESS_PHASES',
  UPDATE_PHASE_STATUS = 'UPDATE_PHASE_STATUS',

  MANAGE_EXPENSE = 'MANAGE_EXPENSE',
  VALIDATE_PAYMENT = 'VALIDATE_PAYMENT',
  ACCESS_FINANCE = 'ACCESS_FINANCE',
  REQUEST_FINANCE_PROVISIONING = 'REQUEST_FINANCE_PROVISIONING',
  ANSWER_FINANCE_PROVISIONING = 'ANSWER_FINANCE_PROVISIONING',

  MANAGE_PROJECT_ACCESS = 'MANAGE_PROJECT_ACCESS',

  VIEW_REPORTS = 'VIEW_REPORTS',
  EXPORT_REPORTS = 'EXPORT_REPORTS',

  NOTES = 'NOTES',

  CREATE_3D_DESIGN = 'CREATE_3D_DESIGN',
  UPDATE_3D_DESIGN = 'UPDATE_3D_DESIGN',
  DELETE_3D_DESIGN = 'DELETE_3D_DESIGN',
  ACCESS_3D_DESIGN = 'ACCESS_3D_DESIGN',

  PROJECT_AUDIT = 'VIEW_PROJECT_AUDIT',

  // Tender permissions
  CREATE_TENDER = 'CREATE_TENDER',
  UPDATE_TENDER = 'UPDATE_TENDER',
  DELETE_TENDER = 'DELETE_TENDER',
  CLOSE_TENDER = 'CLOSE_TENDER',
  ACCESS_TENDERS = 'ACCESS_TENDERS',
  VIEW_TENDER_AUDIT = 'VIEW_TENDER_AUDIT',
  CREATE_TENDER_APPLICATION = 'CREATE_TENDER_APPLICATION',
  UPDATE_TENDER_APPLICATION = 'UPDATE_TENDER_APPLICATION',
  DELETE_TENDER_APPLICATION = 'DELETE_TENDER_APPLICATION',
  ACCESS_TENDER_APPLICATIONS = 'ACCESS_TENDER_APPLICATIONS',
  SUBMIT_TENDER_APPLICATION = 'SUBMIT_TENDER_APPLICATION',
  
  // Project Template permissions
  MANAGE_PROJECT_TEMPLATES = 'MANAGE_PROJECT_TEMPLATES'
}

export enum UserType {
  FIRM_OWNER = 'FIRM_OWNER',
  FIRM_MEMBER = 'FIRM_MEMBER',
  CLIENT = 'CLIENT',
  ADMIN = 'ADMIN'
}
class PermissionService {
  /**
   * Check permission optionally scoped to a projectId.
   * If projectId is provided, we first check projectPermissions[projectId] (if present) and
   * fall back to global permissions. This mirrors backend semantics where project-level grants
   * override or extend global permissions.
   */
  hasPermission(permission: PermissionType | string, projectId?: string | number): boolean {
    const authStore = useAuthStore()
    const user = authStore.getUser as User | null
    if (!user) return false

    // global permissions
    const globalPerms: string[] | undefined = (user as any).permissions || (user as any).roles || []
    const permStr = String(permission)

    // If a projectId is provided, try projectPermissions first
    if (projectId && (user as any).projectPermissions) {
      const projectPerms = (user as any).projectPermissions[String(projectId)] as string[] | undefined
      if (Array.isArray(projectPerms) && projectPerms.includes(permStr)) {
        return true
      }
      // continue to check global perms if not found in project
    }

    return Array.isArray(globalPerms) && globalPerms.includes(permStr)
  }

  hasAnyPermission(permissions: Array<PermissionType | string>, projectId?: string | number): boolean {
    return permissions.some(p => this.hasPermission(p, projectId))
  }

  hasAllPermissions(permissions: Array<PermissionType | string>, projectId?: string | number): boolean {
    return permissions.every(p => this.hasPermission(p, projectId))
  }
}

// Create and export a singleton instance
const permissionService = new PermissionService()
export default permissionService
